import matter from 'gray-matter';
import { remark } from 'remark';
import remarkHtml from 'remark-html';
import remarkGfm from 'remark-gfm';
import remarkSlug from 'remark-slug';

/**
 * 解析 Markdown 文件
 *
 * @deprecated 请使用专门的解析器代替
 * 此函数保留仅用于向后兼容，新代码应使用：
 * - ProjectParser (用于项目)
 * - WritingParser (用于写作)
 * - RecordParser (用于记录)
 *
 * @example
 * ```typescript
 * // 旧用法（不推荐）
 * const result = await parseMarkdown(file);
 *
 * // 新用法（推荐）
 * import { ProjectParser } from './parsers';
 * const parser = new ProjectParser();
 * const result = await parser.parse(file, 'my-project');
 * ```
 */
export async function parseMarkdown(file: string): Promise<{
  frontmatter: Record<string, any>;
  content: string;
}> {
  // 使用 gray-matter 提取 frontmatter 和内容
  const { data, content } = matter(file);

  // 使用 remark 将 markdown 转换为 HTML
  const processedContent = await remark()
    .use(remarkGfm as any) // 支持 GitHub Flavored Markdown (GFM)
    .use(remarkSlug as any) // 为标题添加 ID 属性
    .use(remarkHtml, { sanitize: false }) // 转换为 HTML，不进行sanitize（允许HTML）
    .process(content);

  // 返回 frontmatter 和 HTML 内容
  return {
    frontmatter: data,
    content: processedContent.toString(),
  };
}

/**
 * 从文件路径提取 slug
 *
 * @param path - 文件的完整路径
 * @returns 文件名（不含扩展名）
 *
 * @example
 * ```typescript
 * const slug = extractSlug('/src/content/projects/my-project.md');
 * console.log(slug); // "my-project"
 * ```
 */
export function extractSlug(path: string): string {
  // 获取路径的最后一部分，然后移除 .md 扩展名
  const fileName = path.split('/').pop() || '';
  return fileName.replace('.md', '');
}
